import { roundTo10th } from './Utilities';

export const getHighchartconfig = (
  isResponsive,
  percentage,
) => ({
  chart: {
    width: isResponsive ? '500' : '300',
    height: isResponsive ? '250' : '180',
    type: 'solidgauge',
  },
  colors: ['#002856'],
  title: null,
  pane: {
    center: ['50%', '100%'],
    size: '180%',
    startAngle: -90,
    endAngle: 90,
    background: {
      backgroundColor: '#F4F4F4',
      innerRadius: '80%',
      outerRadius: '100%',
      shape: 'arc',
      borderColor: 'none',
    },
  },
  exporting: {
    enabled: false,
  },
  tooltip: {
    enabled: false,
  },
  yAxis: {
    minorTickInterval: null,
    tickWidth: 1,
    tickColor: '#9B9B9B',
    lineColor: '#fff',
    min: 0,
    max: 100,
    offset: isResponsive ? -50 : -35,
    labels: {
      formatter() {
        return null;
      },
    },
  },
  plotOptions: {
    solidgauge: {
      dataLabels: {
        borderWidth: 0,
        useHTML: true,
      },
    },
  },
  credits: {
    enabled: false,
  },
  series: [
    {
      data: [percentage],
      innerRadius: '80%',
      outerRadius: '100%',
      dataLabels: {
        formatter() {
          if (this.y > 0) {
            const per = (percentage / 20).toFixed(1);
            return (
              '<div style="text-align:center">'
              + `<span style="font-size:56px">${per}</span><br/>`
              + '</div>'
            );
          }
          return '';
        },
      },
    },
  ],
});

function isTooClose(valueA, valueB, tickInterval = 5) {
  return Math.abs(valueA - valueB) <= tickInterval;
}

function getSpaces(
  value,
  {
    currentValue, midValue, baseSpace = 0, tickInterval,
  },
) {
  const spaces = value === midValue && isTooClose(value, currentValue, tickInterval)
    ? (roundTo10th(currentValue)?.toString()?.length || 0) + 4
    : 0;
  return '&nbsp'.repeat(spaces + baseSpace);
}

function getPeerColor(
  orgValue,
  topValue,
  midValue,
  bottomValue,
  value,
) {
  if (value === topValue) {
    return '#757575';
  }
  if (value === midValue) {
    return '#E81059';
  }
  if (value === bottomValue) {
    return '#757575';
  }

  return '#002859';
}

export const getTrendChartConfig = (
  isResponsive,
  {
    orgCurrent,
    orgTarget,
    topCurrent,
    topTarget,
    midCurrent,
    midTarget,
    bottomCurrent,
    bottomTarget,
    unit,
    maximumValue,
    minimumValue,
    tickInterval,
    isPrinting,
    hideTarget,
    hideCurrent,
    width = 360,
    marginLeft,
    marginRight,
    height,
    hideTitle,
    backgroundColor = '#ffffff',
  },
) => {
  const allValues = [
    orgCurrent,
    topCurrent,
    midCurrent,
    bottomCurrent,
    orgTarget,
    topTarget,
    midTarget,
    bottomTarget,
  ].filter(
    (item) => item !== undefined && !Number.isNaN(item) && item !== null,
  );

  const maxValue = maximumValue ?? Math.ceil(Math.max(...allValues) * 1.1);
  const minValue = minimumValue ?? Math.floor(Math.min(...allValues) * 0.9);

  const tickPositionsUnsorted = [
    minValue,
    orgCurrent,
    topCurrent,
    midCurrent,
    bottomCurrent,
    maxValue,
  ].filter(
    (item) => item !== undefined && !Number.isNaN(item) && item !== null,
  );

  const MAX_TICKS = tickInterval
    ? Math.floor((maxValue - minValue) / tickInterval)
    : 10;
  const difference = tickInterval ?? Math.max(Math.floor((maxValue - minValue) / MAX_TICKS), 1);
  const tickIntervalPoints = [];

  if (!isPrinting) {
    for (let i = 1; i <= MAX_TICKS + 1; i += 1) {
      const valueToAdd = minValue + i * difference;
      if (
        !tickPositionsUnsorted.includes(valueToAdd)
        && valueToAdd < maxValue
      ) {
        tickIntervalPoints.push(valueToAdd);
      }
    }
  }

  const tickPositionsCurrentAxis = [
    ...tickIntervalPoints,
    ...tickPositionsUnsorted,
  ].sort((a, b) => a - b);

  const tickPositionsTargetAxis = [
    minValue,
    orgTarget,
    topTarget,
    midTarget,
    bottomTarget,
    maxValue,
    ...tickIntervalPoints,
  ]
    .filter(
      (item) => item !== undefined && !Number.isNaN(item) && item !== null,
    )
    .sort((a, b) => a - b);

  return {
    chart: {
      height: height || (isResponsive ? '300' : '230'),
      spacingTop: 40,
      alignTicks: false,
      spacingLeft: 0,
      spacingRight: 0,
      marginLeft: isResponsive ? '80' : '50',
      marginRight: isResponsive ? '80' : '110',
      animation: false,
      backgroundColor,
      ...(isPrinting ? { width, marginLeft, marginRight } : { width: isResponsive ? '572' : '400' }),
    },

    title: {
      text: '',
      align: 'left',
      margin: 0,
    },

    labels: {
      fontSize: 12,
    },

    yAxis: [
      {
        // Primary yAxis
        min: minValue,
        max: maxValue,
        title: {
          text: `Current value<br />in ${unit}`,
          rotation: 0,
          align: 'high',
          textAlign: 'left',
          offset: 10,
          y: -30,
          style: {
            color: 'black',
            fontSize: 14,
          },
          useHtml: true,
          enabled: !hideTitle,
        },
        tickPositions: tickPositionsCurrentAxis,
        labels: {
          allowOverlap: false,
          x: 0,
          formatter: (chart) => {
            if (
              [orgCurrent, midCurrent].includes(chart.value)
              && chart.value !== null
            ) {
              return `<span style="color: ${getPeerColor(
                orgCurrent,
                topCurrent,
                midCurrent,
                bottomCurrent,
                chart.value,
              )}">
                ${roundTo10th(chart?.value)}${getSpaces(chart.value, {
  midValue: midCurrent,
  baseSpace: 3,
  currentValue: orgCurrent,
  tickInterval,
})}
              </span>`;
            }
            return tickIntervalPoints.includes(chart.value) ? '-' : '';
          },
          style: {
            fontSize: 14,
          },
        },
        lineWidth: 1,
        gridLineWidth: 0,
        minGridLineWidth: 0,
        lineColor: '#3e3e3e',
        visible: !hideCurrent,
      },
      {
        // Secondary yAxis
        min: minValue,
        max: maxValue,
        title: {
          text: `Target value<br /> in ${unit}`,
          rotation: 0,
          align: 'high',
          textAlign: 'right',
          offset: 20,
          y: -30,
          useHtml: true,
          style: {
            fontSize: 14,
            color: 'black',
          },
          enabled: !hideTitle,
        },
        opposite: true,
        tickPositions: tickPositionsTargetAxis,
        labels: {
          allowOverlap: false,
          formatter: (chart) => ([orgTarget, midTarget].includes(chart.value)
          && chart.value !== null
            ? `<span style="color: ${getPeerColor(
              orgTarget,
              topTarget,
              midTarget,
              bottomTarget,
              chart.value,
            )}">
            ${getSpaces(chart.value, {
              midValue: midTarget,
              baseSpace: 0,
              currentValue: orgTarget,
              tickInterval,
            })}${roundTo10th(chart?.value)}
            </span>`
            : ''),
          style: {
            fontSize: 14,
          },
        },
        visible: !hideTarget,
        lineWidth: 1,
        gridLineWidth: 0,
        minGridLineWidth: 0,
        lineColor: '#3e3e3e',
      },
    ],

    xAxis: {
      labels: {
        enabled: false,
      },
      visible: !hideTarget && !hideCurrent,
      categories: ['Current', 'Target'],
    },

    legend: {
      enabled: false,
    },

    series: [
      {
        name: '',
        color: '#002856',
        lineWidth: 4,
        data: [
          {
            id: 'myOrgCurrent',
            name: 'My Org Current',
            y: orgCurrent,
            value: orgCurrent,
            text: '75th Percentitle',
            marker: {
              enabled: false,
              symbol: 'triangle',
            },
          },
          {
            id: 'myOrgTarget',
            name: 'My Org Target',
            y: orgTarget,
            value: orgTarget,
            text: '75th Percentitle',
            marker: {
              enabled: false,
              symbol: 'triangle',
            },
            visible: !hideTarget,
          },
        ],
        zIndex: 105,
      },
      {
        name: '',
        color: 'rgb(166, 166, 166)',
        data: [
          {
            id: 'topPeerCurrent',
            name: 'Top Peer Current',
            y: topCurrent,
            value: topCurrent,
            marker: {
              enabled: false,
              symbol: 'square',
            },
          },
          {
            id: 'topPeerTarget',
            name: 'Top Peer Target',
            y: topTarget,
            value: topTarget,
            marker: {
              enabled: false,
              symbol: 'square',
            },
            visible: !hideTarget,
          },
        ],
        zIndex: 101,
      },
      {
        name: '',
        color: '#E81059',
        lineWidth: 3,
        data: [
          {
            id: 'midCurrent',
            name: 'Mid Peer Current',
            y: midCurrent,
            value: midCurrent,
            text: '25th Percentitle',
            marker: {
              enabled: false,
              symbol: 'diamond',
            },
          },
          {
            id: 'midTarget',
            name: 'Mid Peer Target',
            y: midTarget,
            value: midTarget,
            text: '25th Percentitle',
            marker: {
              enabled: false,
              symbol: 'diamond',
            },
            visible: !hideTarget,
          },
        ],
        zIndex: 102,
      },
      {
        name: '',
        color: 'rgb(166, 166, 166)',
        data: [
          {
            id: 'bottomPeerCurrent',
            name: 'Bottom Peer Current',
            y: bottomCurrent,
            value: bottomCurrent,
            text: '50th Percentitle',
            marker: {
              enabled: false,
              symbol: 'square',
            },
          },
          {
            id: 'bottomPeerTarget',
            name: 'Bottom Peer Target',
            y: bottomTarget,
            value: bottomTarget,
            text: '50th Percentitle',
            marker: {
              enabled: false,
              symbol: 'square',
            },
            visible: !hideTarget,
          },
        ],
        zIndex: 103,
      },
    ],

    annotations: [
      {
        crop: false,
        draggable: '',
        shapes: [

          {
            point: 'topPeerCurrent',
            type: 'rect',
            width: 20,
            height: (!!topCurrent || topCurrent === 0) ? 1 : 0,
            fill: 'rgb(166, 166, 166)',
            stroke: 'rgb(166,166,166)',
            x: -10,
            y: -0.5,
            zIndex: 1000,
          },
          {
            point: 'topPeerTarget',
            type: 'rect',
            width: 20,
            height: (!!topTarget || topTarget === 0) ? 1 : 0,
            fill: 'rgb(166, 166, 166)',
            stroke: 'rgb(166,166,166)',
            x: -10,
            y: -0.5,
            zIndex: 1000,
          },
          {
            point: 'bottomPeerCurrent',
            type: 'rect',
            width: 20,
            height: (!!bottomCurrent || bottomCurrent === 0) ? 1 : 0,
            fill: 'rgb(166, 166, 166)',
            stroke: 'rgb(166,166,166)',
            x: -10,
            y: -0.5,
            zIndex: 10,
          },
          {
            point: 'bottomPeerTarget',
            type: 'rect',
            width: 20,
            height: (!!bottomTarget || bottomTarget === 0) ? 1 : 0,
            fill: 'rgb(166, 166, 166)',
            stroke: 'rgb(166,166,166)',
            x: -10,
            y: -0.5,
            zIndex: 10,
          },
          {
            point: 'myOrgCurrent',
            type: 'rect',
            width: 20,
            height: (!!orgCurrent || orgCurrent === 0) ? 4 : 0,
            fill: '#002856',
            stroke: '#002856',
            x: -10,
            y: -3,
            zIndex: 10,
          },
          {
            point: 'myOrgTarget',
            type: 'rect',
            width: 20,
            height: (!!orgTarget || orgTarget === 0) ? 4 : 0,
            fill: '#002856',
            stroke: '#002856',
            x: -10,
            y: -3,
            zIndex: 10,
          },
          {
            point: 'midCurrent',
            type: 'rect',
            width: 20,
            height: (!!midCurrent || midCurrent === 0) ? 3 : 0,
            fill: '#E81059',
            stroke: '#E81059',
            x: -10,
            y: -1.5,
            zIndex: 10,
          },
          {
            point: 'midTarget',
            type: 'rect',
            width: 20,
            height: (!!midTarget || midTarget === 0) ? 3 : 0,
            fill: '#E81059',
            stroke: '#E81059',
            x: -10,
            y: -1.5,
            zIndex: 10,
          },
          {
            points: ['topPeerCurrent', 'bottomPeerCurrent'],
            strokeWidth: 20,
            type: 'path',
            stroke: 'rgb(217, 217, 217)',
          },
          {
            points: ['topPeerTarget', 'bottomPeerTarget'],
            strokeWidth: 20,
            type: 'path',
            stroke: 'rgb(217, 217, 217)',
          },
        ],
        zIndex: 1,
      },
    ],

    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              enabled: false,
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
          },
        },
      ],
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      series: {
        pointPlacement: 'on',
        animation: false,
      },
    },
    tooltip: {
      headerFormat: '<span style="font-size:11px">{series.name}</span><br>',
      pointFormat: `<span>{point.name}</span>: <b>{point.value} ${unit}</b>`,
    },
  };
};
